/**
 * The rail a train is on: points along the track from its front back past
 * its last car, laid by its front as it goes (`three/trains.ts`). Every car
 * stands on this, each bogie at its own distance behind the front, so the
 * cars bend through a curve and go where the front went, as a tram's
 * sections do.
 *
 * Pure: points in, points out.
 */

export interface RailPoint {
  x: number;
  y: number;
  z: number;
}

export class RailPath {
  /** Front first. */
  private readonly points: RailPoint[] = [];
  /** Each point's distance behind the front, metres. */
  private readonly behind: number[] = [];

  get length(): number {
    return this.behind.length === 0 ? 0 : this.behind[this.behind.length - 1];
  }

  get size(): number {
    return this.points.length;
  }

  get front(): RailPoint | undefined {
    return this.points[0];
  }

  /** The front moved on to `p`: everything else is that much further behind it. */
  advance(p: RailPoint): void {
    const f = this.points[0];
    const d = f === undefined ? 0 : Math.hypot(p.x - f.x, p.y - f.y, p.z - f.z);
    for (let i = 0; i < this.behind.length; i++) this.behind[i] += d;
    this.points.unshift({ ...p });
    this.behind.unshift(0);
  }

  /** Lay a point behind the back: building the rail behind a train set down. */
  extendBack(p: RailPoint): void {
    const b = this.points[this.points.length - 1];
    const d = b === undefined ? 0 : Math.hypot(p.x - b.x, p.y - b.y, p.z - b.z);
    this.points.push({ ...p });
    this.behind.push((this.behind[this.behind.length - 1] ?? 0) + d);
  }

  /** Let go of what is more than `keep` metres behind the front (but always the point past it). */
  trim(keep: number): void {
    const last = this.behind.findIndex(d => d > keep);
    if (last < 0) return;
    this.points.length = last + 1;
    this.behind.length = last + 1;
  }

  /** The same rail the other way: its back now its front, as a train turning back at the end of the line. */
  reversed(): RailPath {
    const out = new RailPath();
    for (let i = this.points.length - 1; i >= 0; i--) out.extendBack(this.points[i]);
    return out;
  }

  /** Move every point by (dx, dz): the scene's origin moved. */
  shift(dx: number, dz: number): void {
    for (const p of this.points) { p.x += dx; p.z += dz; }
  }

  /**
   * The point `d` metres behind the front along the rail, written into
   * `out`; past the back, the back. Undefined with no rail.
   */
  at(d: number, out: RailPoint): RailPoint | undefined {
    const n = this.points.length;
    if (n === 0) return undefined;
    if (d <= 0 || n === 1) return Object.assign(out, this.points[0]);
    // Binary search for the first point at least `d` behind.
    let lo = 0, hi = n - 1;
    if (d >= this.behind[hi]) return Object.assign(out, this.points[hi]);
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (this.behind[mid] < d) lo = mid; else hi = mid;
    }
    const a = this.points[lo], b = this.points[hi];
    const span = this.behind[hi] - this.behind[lo];
    const t = span > 1e-9 ? (d - this.behind[lo]) / span : 0;
    out.x = a.x + (b.x - a.x) * t;
    out.y = a.y + (b.y - a.y) * t;
    out.z = a.z + (b.z - a.z) * t;
    return out;
  }
}
